import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, TrendingUp, Sparkles, Recycle, Award, ShieldCheck, Zap, Users } from "lucide-react";
import { api } from "@/lib/api";
import { handleBuyClick } from "@/lib/buy";
import ProductCard from "@/components/ProductCard";
import EmptyProducts from "@/components/EmptyProducts";
import SustainabilityScore from "@/components/SustainabilityScore";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import Faq, { faqJsonLd } from "@/components/Faq";
import { Section } from "@/components/seo/SeoLayout";

const SECTION_ICONS = {
  green_picks: <Leaf className="h-5 w-5" />,
  top_cashback: <TrendingUp className="h-5 w-5" />,
  most_sustainable: <Award className="h-5 w-5" />,
  trending: <Sparkles className="h-5 w-5" />,
  eco_alternatives: <Recycle className="h-5 w-5" />,
};

const SECTION_META = {
  green_picks: { title: "🌱 Green Picks of the Week", subtitle: "Curated planet-friendly finds" },
  top_cashback: { title: "🔥 Highest Cashback", subtitle: "Save more on every purchase" },
  most_sustainable: { title: "⭐ Most Sustainable", subtitle: "Highest sustainability scores" },
  trending: { title: "💰 Trending Deals", subtitle: "What everyone's buying right now" },
  eco_alternatives: { title: "♻️ Eco Alternatives", subtitle: "Better swaps for everyday products" },
};

const Rail = ({ id, title, subtitle, items, onBuy }) => (
  <section className="mb-16" data-testid={`section-${id}`}>
    <div className="flex items-end justify-between mb-6 px-1">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      </div>
      <Link to="/products" className="hidden sm:inline text-sm font-medium text-primary hover:underline">
        View all →
      </Link>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {items?.slice(0, 4).map((p) => (
        <ProductCard key={p.id} product={p} onBuy={onBuy} />
      ))}
    </div>
  </section>
);

const HOMEPAGE_FAQ = [
  { q: "What is Ecowallet?", a: "Ecowallet is an AI-powered sustainable shopping platform. It analyses publicly available product information to generate transparent Sustainability Scores (0–100), helps you find greener alternatives, and rewards every purchase with cashback and Green Points." },
  { q: "How does Ecowallet's AI Sustainability Score work?", a: "Our AI (Claude Sonnet 4.6) reviews a product's brand website, merchant page and declared certifications, then scores it on seven measurable dimensions — carbon footprint, packaging, materials, recyclability, manufacturing, shipping and certifications — with a confidence rating and per-metric reasoning we publish transparently." },
  { q: "Can I earn cashback for sustainable shopping?", a: "Yes. Every purchase earns real cashback (3%–18%) plus Green Points that can be redeemed for gift cards, coupons, tree plantation or NGO donations." },
  { q: "How do I find sustainable alternatives to a product?", a: "Open any product page and scroll to 'Better Green Alternatives' — Ecowallet automatically surfaces the highest-scoring products in the same category, with side-by-side score comparison and clear reasons the alternative is greener." },
  { q: "How does Ecowallet detect greenwashing?", a: "Our AI weighs certifications and verifiable evidence far more than vague marketing language. Whenever a claim can't be substantiated in the sources analysed, the Sustainability Score reflects that honestly — and the reasoning is published on the product page." },
  { q: "Is there an app that rates products for sustainability?", a: "Yes — Ecowallet does exactly this. Every product on the platform has a 0–100 AI Sustainability Score plus per-metric reasoning, and you can search or filter across 100+ sustainable products by category, score or cashback." },
];

const HomePage = () => {
  useSEO({
    title: "Ecowallet – AI Sustainability Scores & Rewards for Sustainable Shopping",
    description: "Discover sustainable products with AI-powered sustainability scores. Compare products, find greener alternatives and earn cashback and rewards for sustainable shopping with Ecowallet.",
    keywords: "sustainable shopping, sustainable shopping app, AI sustainability score, eco score, sustainable products, sustainable alternatives, cashback for sustainable purchases, greenwashing checker",
    jsonLd: faqJsonLd(HOMEPAGE_FAQ),
  });
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/homepage");
        setSections(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleBuy = (product) => handleBuyClick(product, { user });

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-50 via-background to-amber-50/40 dark:from-emerald-950/30 dark:via-background dark:to-amber-950/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-600/30 bg-emerald-600/10 text-emerald-700 dark:text-emerald-300 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest">
              <Leaf className="h-3.5 w-3.5" /> The smarter way to shop sustainably
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Shop Sustainably.<br />
              <span className="text-primary">Get Rewarded.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
              Ecowallet uses AI to analyse publicly available product information and generate transparent
              Sustainability Scores — helping you make better purchasing decisions while earning cashback and
              rewards for sustainable choices.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" className="btn-pill bg-primary text-primary-foreground" data-testid="hero-explore-btn">
                Explore Products <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
              <Link to="/about" className="btn-pill border border-border bg-card" data-testid="hero-how-btn">
                How It Works
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "100+", l: "Products" },
                { v: "8", l: "Categories" },
                { v: "5", l: "Level Badges" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="mono text-2xl font-bold">{s.v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            {/* Aesthetic composition: minimalist sustainable shopping photo backdrop with floating assessment cards around a central score orb. */}
            <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] border border-border/60 shadow-2xl shadow-emerald-500/10">
              <img
                src="https://images.unsplash.com/photo-1544816155-12df9643f363?fm=jpg&q=80&w=1200&auto=format&fit=crop"
                alt="Minimalist kraft paper shopping bag — sustainable shopping"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              {/* Soft tint overlay so the score orb + glass chips remain legible against the photo */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/10 to-white/40 dark:from-slate-900/40 dark:via-slate-900/20 dark:to-slate-900/50" />

              {/* Central score orb */}
              <motion.div
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.7 }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-3xl bg-emerald-400/40 dark:bg-emerald-500/25 -z-10" />
                  <div className="rounded-full bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border border-white/70 dark:border-white/10 shadow-2xl p-5">
                    <SustainabilityScore score={87} size={130} stroke={10} showLabel={false} showGrade={true} />
                  </div>
                </div>
              </motion.div>

              {/* Verified certifications chip (top-left) */}
              <motion.div
                initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute left-5 top-6 rounded-2xl bg-white/85 dark:bg-slate-900/70 backdrop-blur-md border border-white/60 dark:border-white/10 px-3 py-2 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Verified</div>
                    <div className="text-xs font-bold text-foreground">GOTS · FSC</div>
                  </div>
                </div>
              </motion.div>

              {/* Source tier chip (top-right, branded gradient) */}
              <motion.div
                initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="absolute right-5 top-6 rounded-2xl px-3 py-2 shadow-lg text-white"
                style={{ background: "linear-gradient(135deg,#10B981 0%,#059669 100%)" }}
              >
                <div className="text-[9px] uppercase tracking-widest opacity-80">Source strength</div>
                <div className="text-xs font-bold">Tier A · Peer reviewed</div>
              </motion.div>

              {/* Mini breakdown card (mid-right) */}
              <motion.div
                initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute right-5 top-1/2 translate-y-4 rounded-2xl bg-white/85 dark:bg-slate-900/70 backdrop-blur-md border border-white/60 dark:border-white/10 px-3 py-2.5 shadow-lg w-[136px]"
              >
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Breakdown</div>
                {[
                  { l: "Materials", v: 92, c: "#10B981" },
                  { l: "Packaging", v: 78, c: "#22D3A5" },
                  { l: "Circularity", v: 84, c: "#059669" },
                ].map((b) => (
                  <div key={b.l} className="mb-1 last:mb-0">
                    <div className="flex justify-between text-[10px] font-semibold">
                      <span>{b.l}</span><span className="mono">{b.v}</span>
                    </div>
                    <div className="h-1 rounded-full bg-muted mt-0.5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${b.v}%`, background: b.c }} />
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Evidence chip (mid-left) */}
              <motion.div
                initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.85, duration: 0.6 }}
                className="absolute left-5 top-1/2 -translate-y-2 rounded-2xl bg-white/85 dark:bg-slate-900/70 backdrop-blur-md border border-white/60 dark:border-white/10 px-3 py-2 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                    <Recycle className="h-3 w-3" />
                  </span>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Recycled content</div>
                    <div className="text-[11px] font-bold text-foreground">Verified · 80%</div>
                  </div>
                </div>
              </motion.div>

              {/* Bottom band */}
              <motion.div
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.0, duration: 0.6 }}
                className="absolute left-5 right-5 bottom-5"
              >
                <div className="rounded-2xl bg-white/90 dark:bg-slate-900/80 backdrop-blur-md border border-white/60 dark:border-white/10 px-4 py-3 shadow-lg">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold">
                    <Sparkles className="h-3 w-3" /> Evidence-based scoring
                  </div>
                  <div className="mt-1 text-[13px] font-semibold leading-snug text-foreground">
                    Every score is deterministic, versioned and traceable to a source.
                  </div>
                </div>
              </motion.div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* SECTIONS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse aspect-[3/4] rounded-3xl bg-muted" />
            ))}
          </div>
        ) : (
          <>
            {(() => {
              const railKeys = ["green_picks", "top_cashback", "most_sustainable", "trending", "eco_alternatives"];
              const anyProducts = railKeys.some((k) => sections[k]?.length);
              if (!anyProducts) return <EmptyProducts />;
              return railKeys.map((k) =>
                sections[k]?.length ? (
                  <Rail key={k} id={k} {...SECTION_META[k]} items={sections[k]} onBuy={handleBuy} />
                ) : null
              );
            })()}
          </>
        )}
      </div>

      {/* SEO CONTENT SECTIONS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Section id="what-is-ecowallet" title="What is Ecowallet?" kicker="Sustainable shopping companion">
          <p>Ecowallet is an <strong>AI-powered sustainable shopping platform</strong>. It analyses publicly available information about products and generates a transparent Sustainability Score, helping you make better purchasing decisions while earning cashback and rewards for sustainable choices.</p>
          <p>Unlike a generic cashback website or eco-friendly directory, Ecowallet combines three things no one else does together: <strong>AI-powered product sustainability intelligence</strong>, <strong>curated sustainable product discovery</strong>, and <strong>real cashback plus gamified rewards</strong> for every eco-friendly choice.</p>
        </Section>

        <Section id="how-score-works" title="How the Sustainability Score works" kicker="Transparent 0–100 rating">
          <p>Every product on Ecowallet is scored by AI across seven measurable dimensions — <strong>carbon footprint, packaging, materials, recyclability, manufacturing, shipping, and certifications</strong>. Each metric is scored 0–100 with per-metric reasoning we publish transparently, plus a confidence rating so you know how much evidence backs the score. <Link to="/sustainability-score" className="text-primary font-semibold hover:underline">Read the full methodology →</Link></p>
        </Section>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { icon: <Sparkles className="h-5 w-5" />, t: "Find Sustainable Products", d: "Browse 100+ sustainable products across 8 categories with transparent AI scores.", to: "/sustainable-products" },
            { icon: <Recycle className="h-5 w-5" />, t: "Compare Alternatives", d: "See side-by-side sustainability comparisons and switch to greener options.", to: "/sustainable-alternatives" },
            { icon: <Award className="h-5 w-5" />, t: "Earn Cashback & Rewards", d: "3–18% cashback + Green Points for every purchase, redeemable for gift cards or tree plantation.", to: "/sustainable-shopping-rewards" },
            { icon: <Zap className="h-5 w-5" />, t: "AI-Powered Intelligence", d: "Claude Sonnet 4.6 reasons over public product data to score each item on seven dimensions.", to: "/ai-sustainability" },
            { icon: <ShieldCheck className="h-5 w-5" />, t: "Detect Greenwashing", d: "Our AI treats vague eco marketing skeptically and rewards verifiable evidence only.", to: "/greenwashing" },
            { icon: <Users className="h-5 w-5" />, t: "Leaderboards & Challenges", d: "Compete on Green Points, unlock badges from Eco Beginner to Net Zero Champion.", to: "/leaderboard" },
          ].map((f) => (
            <Link key={f.t} to={f.to} className="card-premium p-6 hover:-translate-y-1 transition-all group">
              <div className="grid place-items-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{f.icon}</div>
              <h3 className="mt-4 font-bold text-lg">{f.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
              <div className="mt-4 text-sm text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Learn more →</div>
            </Link>
          ))}
        </div>

        {/* FAQ */}
        <section id="faq" className="mt-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Frequently Asked Questions</h2>
          <p className="text-muted-foreground mt-3">Everything about sustainable shopping, AI sustainability scores, and Ecowallet's rewards.</p>
          <div className="mt-6">
            <Faq items={HOMEPAGE_FAQ} testId="faq-home" />
          </div>
        </section>
      </div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="card-premium p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-100/60 to-amber-100/40 dark:from-emerald-950/40 dark:to-amber-950/20" />
          <div className="max-w-2xl">
            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight">Every purchase has an impact.</h3>
            <p className="mt-3 text-muted-foreground text-lg">
              We help you make better choices — and reward you for them. Join thousands turning everyday shopping into
              real environmental action.
            </p>
            <Link to="/signup" className="btn-pill mt-6 bg-primary text-primary-foreground" data-testid="footer-cta-signup">
              Start earning Green Points →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
